type SimpleToken = '(' | ')' | '+' | '*' | '/' | '^' | '.' | ',' | '=' | '{' | '}' | ';';
type CharTokens = SimpleToken | '<' | '>' | '<=' | '>=' | '!=' | '-' | 'EOF';

export class CharToken {
  public readonly type: CharTokens;
  public readonly index: number;

  constructor(type: CharTokens, index: number) {
    this.type = type;
    this.index = index;
  }
}

export class StringToken {
  public readonly type = 'String';
  public raw: string;
  public readonly units: { unit: string; exponent: number }[];
  public readonly index: number;

  constructor(raw: string, units: { unit: string; exponent: number }[], index: number) {
    this.raw = raw;
    this.units = units;
    this.index = index;
  }
}

export class IdentifierToken {
  public readonly type = 'Identifier';
  public readonly name: string;
  public readonly annotation: string;
  public readonly index: number;

  constructor(name: string, annotation: string, index: number) {
    this.name = name;
    this.annotation = annotation;
    this.index = index;
  }
}

export class NumberToken {
  public readonly type = 'Number';
  public readonly number: number;
  public readonly units: { unit: string; exponent: number }[];
  public readonly index: number;

  constructor(number: number, units: { unit: string; exponent: number }[], index: number) {
    this.number = number;
    this.units = units;
    this.index = index;
  }
}

export class UnitConversionToken {
  public readonly type = 'UnitConversion';
  public readonly units: { unit: string; exponent: number }[];
  public readonly index: number;

  constructor(units: { unit: string; exponent: number }[], index: number) {
    this.units = units;
    this.index = index;
  }
}

export type Token = CharToken | IdentifierToken | NumberToken | StringToken | UnitConversionToken;

const simpleTokens = new Set<string>('()+*/^.,={};'.split(''));
function isSimpleToken(char: string): char is SimpleToken {
  return simpleTokens.has(char);
}

function isDigit(char: string): boolean {
  return /[0-9]/.test(char);
}

function isAlpha(char: string): boolean {
  return /[a-zA-Z]/.test(char);
}

function isIdentifierAlphaNumeric(char: string): boolean {
  return /[a-zA-Z0-9_]/.test(char);
}

function isUnitsAlpha(char: string): boolean {
  return /[a-zA-Z%]/.test(char);
}

export function unitsToString(units: { unit: string; exponent: number }[]) {
  const numerator = units
    .filter(({ exponent }) => exponent > 0)
    .map(({ unit, exponent }) => {
      if (exponent !== 1) {
        return `${unit}^${exponent}`;
      } else {
        return unit;
      }
    })
    .join('-');

  // Same as above, but negate the exponent so we can display it underneath a slash:
  const denominator = units
    .filter(({ exponent }) => exponent < 0)
    .map(({ unit, exponent }) => {
      if (exponent !== -1) {
        return `${unit}^${-exponent}`;
      } else {
        return unit;
      }
    })
    .join('-');

  if (numerator && denominator) {
    return numerator + '/' + denominator;
  } else if (numerator) {
    return numerator;
  } else {
    // Recompute the denominator without the negated exponent (i.e. m^-3 instead of 1/m^3):
    return units
      .map(({ unit, exponent }) => {
        if (exponent !== 1) {
          return `${unit}^${exponent}`;
        } else {
          return unit;
        }
      })
      .join('-');
  }
}

export function tokenToString(token: Token): string {
  if (token instanceof CharToken) {
    if (token.type === 'EOF') {
      return '';
    }

    return token.type;
  } else if (token instanceof IdentifierToken) {
    if (token.annotation) {
      return token.name + '[' + token.annotation + ']';
    } else {
      return token.name;
    }
  } else if (token instanceof NumberToken) {
    return (token.number + ' ' + unitsToString(token.units)).trim();
  } else if (token instanceof StringToken) {
    return `"${token.raw}"`;
  } else if (token instanceof UnitConversionToken) {
    return '-> ' + unitsToString(token.units);
  } else {
    throw new Error(`Refusing to stringify unknown token: ${JSON.stringify(token)}`);
  }
}

export function tokensToString(tokens: Token[]): string {
  return tokens.map(tokenToString).join(' ');
}

export class Tokenizer {
  static tokenize(input: string): { error: string; index: number; tokens: Token[] } | { tokens: Token[] } {
    const tokenizer = new Tokenizer(input);
    const tokens: Token[] = [];

    while (!tokenizer.isAtEnd()) {
      const index = tokenizer.index;
      const char = tokenizer.advance();
      if (/\s/.test(char)) {
        // Ignore whitespace.
      } else if (isSimpleToken(char)) {
        tokens.push(new CharToken(char, index));
      } else if (char === '<' || char === '>') {
        if (tokenizer.peek() === '=') {
          tokenizer.advance();
          tokens.push(new CharToken((char + '=') as '<=' | '>=', index));
        } else {
          tokens.push(new CharToken(char, index));
        }
      } else if (char === '!' && tokenizer.peek() === '=') {
        tokenizer.advance();
        tokens.push(new CharToken('!=', index));
      } else if (char === '-') {
        // Unit conversion (->):
        if (tokenizer.peek() === '>') {
          tokenizer.advance();
          const units = tokenizer.units();
          if ('error' in units) {
            return { error: units.error, index: units.index, tokens };
          }

          if (units.units.length === 0) {
            return { error: 'Expected units after conversion (->).', index: index, tokens };
          }

          tokens.push(new UnitConversionToken(units.units, index));
        } else {
          tokens.push(new CharToken('-', index));
        }
      } else if (char === "'") {
        while (tokenizer.peek() !== "'" && !tokenizer.isAtEnd()) {
          tokenizer.advance();
        }
        tokenizer.advance(); // Consume "'"

        const raw = input.slice(index + 1, tokenizer.index - 1);
        const units = tokenizer.units();
        if ('error' in units) {
          return { error: units.error, index: units.index, tokens };
        }

        tokens.push(new StringToken(raw, units.units, index));
      } else if (isDigit(char)) {
        const number = tokenizer.number(index, true);
        const units = tokenizer.units();
        if ('error' in units) {
          return { error: units.error, index: units.index, tokens };
        }

        tokens.push(new NumberToken(number, units.units, index));
      } else if (isAlpha(char)) {
        while (isIdentifierAlphaNumeric(tokenizer.peek())) {
          tokenizer.advance();
        }

        const name = input.slice(index, tokenizer.index);
        let annotation = '';
        if (tokenizer.peek() === '[') {
          tokenizer.advance();
          const start = tokenizer.index;
          while (!tokenizer.isAtEnd() && tokenizer.peek() !== ']') {
            tokenizer.advance();
          }
          if (tokenizer.peek() === ']') {
            tokenizer.advance();
          } else {
            return { error: `Invalid identifier. Expected ']' but found ${tokenizer.peek()}.`, index, tokens };
          }

          annotation = input.slice(start, tokenizer.index - 1);
        }
        tokens.push(new IdentifierToken(name, annotation, index));
      } else {
        return { error: `Invalid formula. Unexpected character ${char}.`, index, tokens };
      }
    }

    tokens.push(new CharToken('EOF', tokenizer.index));

    return { tokens };
  }

  static tokenizeUnits(
    input: string,
  ): { error: string; index: number } | { units: { unit: string; exponent: number }[] } {
    return new Tokenizer(input).units();
  }

  index = 0;
  public readonly input: string;

  private constructor(input: string) {
    this.input = input;
  }

  advance(): string {
    this.index += 1;
    return this.input[this.index - 1];
  }

  peek(): string {
    if (this.index >= this.input.length) {
      return '\0';
    }

    return this.input[this.index];
  }

  isAtEnd(): boolean {
    return this.index >= this.input.length;
  }

  number(startIndex: number, includeDecimals: boolean): number {
    while (isDigit(this.peek())) {
      this.advance();
    }

    if (includeDecimals && this.peek() === '.') {
      this.advance();
      while (isDigit(this.peek())) {
        this.advance();
      }
    }

    return parseFloat(this.input.slice(startIndex, this.index));
  }

  units(): { error: string; index: number } | { units: { unit: string; exponent: number }[] } {
    const units: { unit: string; exponent: number }[] = [];

    while (/\s/.test(this.peek())) {
      this.advance();
    }

    let numerator = true;
    while (true) {
      if (!isUnitsAlpha(this.peek())) {
        break;
      }

      const unitNameStartIndex = this.index;
      while (isUnitsAlpha(this.peek())) {
        this.advance();
      }
      const unit = this.input.slice(unitNameStartIndex, this.index);

      let exponent = 1;
      if (this.peek() === '^') {
        this.advance();

        let makeNegative = false;
        if (this.peek() === '-') {
          this.advance();
          makeNegative = true;
        }
        exponent = this.number(this.index, false);
        if (makeNegative) {
          exponent *= -1;
        }
      }
      if (!numerator) {
        exponent = -exponent;
      }

      units.push({ unit, exponent });

      // Unit separator, e.g. the "-" in "ft-lbs"
      if (this.peek() === '-') {
        this.advance();
      }

      if (this.peek() === '/') {
        if (numerator === false) {
          return { error: 'Cannot have two denominators for units.', index: this.index };
        }
        numerator = false;
        this.advance();
      }
    }

    return { units };
  }
}
